import { Tag } from "@fanduel/formation-react-components";
import Image from "next/image";
import Link from "next/link";
import PortableText from "@/components/PortableText/PortableText";
import AuthorSocialLinks from "@/components/social/AuthorSocialLinks";
import Shared from "@/components/social/Shared";
import { AUTHOR_FAVORITE_SPORT_CLICKED } from "@/constants/amplitude";
import { ContentAuthor, Sport } from "@/graphql/gql/graphql";
import { getTwitterAccount, getFacebookAccount } from "@/utils/author";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useLinkCopiedNotification } from "@/utils/hooks/useLinkCopiedNotification";
import styles from "./AuthorInfo.module.scss";

interface AuthorInfoProps {
  author: ContentAuthor;
}

export default function AuthorInfo({ author }: AuthorInfoProps) {
  const { showNotification, notify } = useLinkCopiedNotification();
  const { logAmplitudeEvent } = useAmplitude();
  const ampFavoriteSports = JSON.stringify(
    author.favoriteSports?.map((sport) => sport?.name)
  );

  const trackSportTag = (sport: Sport) => {
    logAmplitudeEvent(AUTHOR_FAVORITE_SPORT_CLICKED, {
      Module: "favorite_sports",
      "Link URL": `/${sport.slug}/`,
      "Link Type": "cta",
      Author: author.name,
      "Favorite Competitions": ampFavoriteSports,
      "Link Text": sport.name,
    });
  };

  const twitterAccount = author.socialMedia
    ? (getTwitterAccount(author.socialMedia)?.url ?? "")
    : "";
  const facebookAccount = author.socialMedia
    ? (getFacebookAccount(author.socialMedia)?.url ?? "")
    : "";

  return (
    <>
      <div className={styles.cover}>
        <Image
          src="https://cdn.sanity.io/images/pbwem9y5/ch_production/4a5010a28685117ad728aff4eab70843b81e27d2-2833x2121.png"
          alt="Background cover"
          fill
          style={{ objectFit: "cover" }}
          quality={100}
          priority
          className={styles.backgroundImage}
        />
        {author.profileImageUrl && (
          <div className={styles.authorImage}>
            <Image
              src={author.profileImageUrl}
              alt={`${author.name}'s picture`}
              width={131}
              height={131}
              priority
            />
          </div>
        )}
      </div>
      <div>
        <div>
          <div className={styles.authorInfoRow}>
            <h2 className={styles.authorName}>{author.name}</h2>
            <AuthorSocialLinks
              twitterUrl={twitterAccount}
              facebookUrl={facebookAccount}
              dispatchFunction={notify}
              authorName={author.name}
            />
          </div>
        </div>
        <div className={styles.authorBio} data-testid="author-bio">
          {author.bio && <PortableText value={JSON.parse(author.bio)} />}
        </div>
        {author.favoriteSports?.length ? (
          <div className={styles.favoriteSports}>
            <h3>Favorite Sports</h3>
            <div className={styles.sportsTags}>
              {author.favoriteSports.map((sport) => {
                const sportSlug = sport?.slug;
                const sportName = sport?.name;

                if (!sportSlug || !sportName) return null;

                return (
                  <Link href={`/${sport.slug}/`} passHref key={sportSlug}>
                    <Tag
                      intent="info"
                      size="lg"
                      variant="outline"
                      className={styles.sportTag}
                      onClick={() => {
                        trackSportTag(sport);
                      }}
                    >
                      {sportName}
                    </Tag>
                  </Link>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      <div className={styles.sharedContainer}>
        <div className={styles.sharedSubContainer}>
          {showNotification && <Shared />}
        </div>
      </div>
    </>
  );
}
